import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { Flag, MapPin, Shield, Trophy, User, UserRound } from 'lucide-react';
import IntermediateStepsPanel from './ChatSearchSteps/IntermediateStepsPanel';
import CitationHighlight from './CitationHighlight';
import EntityHighlight from './EntityHighlight';
import { buildHighlightedMarkdown } from '../../helpers/citations';
import { ENTITY_TYPES, buildHighlightedEntitiesMarkdown, extractEntities, extractToolResults } from '../../helpers/entities';

const REMARK_PLUGINS = [remarkGfm];
const REHYPE_PLUGINS = [rehypeRaw];
const SWIPER_MODULES = [Navigation];

const TYPE_ICONS = {
  player: User,
  coach: UserRound,
  referee: Flag,
  team: Shield,
  league: Trophy,
  venue: MapPin,
};

const MARKDOWN_COMPONENTS = {
  table: ({ node, children, ...props }) => (
    <div className="chat-table-wrap">
      <table {...props}>{children}</table>
    </div>
  ),
};

const toggleButtonClass = active =>
  `flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? 'border-transparent bg-[linear-gradient(135deg,rgb(90,107,122),rgb(123,135,148),rgb(154,170,184))] text-ink'
      : 'border-shell-border bg-paper-2 text-mute hover:border-primary/40 hover:text-ink'
  }`;

const ChatMessageItem = ({ elem, ind, user, onSelectSource, onSelectEntity, onVisible }) => {
  const initial = user?.first_name?.trim()?.[0]?.toUpperCase() || 'U';
  const [showSources, setShowSources] = useState(false);
  const [activeTypes, setActiveTypes] = useState({});
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el || !onVisible) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onVisible(ind);
        }
      },
      { root: null, rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ind, onVisible]);

  const hasSources = (elem?.source?.length || 0) > 0;

  const entities = useMemo(() => extractEntities(elem?.intermediate_steps), [elem?.intermediate_steps]);
  const toolResults = useMemo(() => extractToolResults(elem?.intermediate_steps), [elem?.intermediate_steps]);
  const presentTypes = useMemo(
    () => Object.keys(entities).filter(type => entities[type].length > 0),
    [entities],
  );
  const hasEntities = presentTypes.length > 0;

  const highlighted = useMemo(() => {
    if (!showSources || elem?.isStreaming) return { markdown: elem?.ai || '', citeById: {} };
    return buildHighlightedMarkdown(elem?.ai, elem?.source, elem?.images);
  }, [elem?.ai, elem?.source, elem?.images, elem?.isStreaming, showSources]);

  const entityHighlighted = useMemo(() => {
    const active = presentTypes.filter(type => activeTypes[type]);
    if (!active.length || elem?.isStreaming) return { markdown: highlighted.markdown, entityById: {} };
    return buildHighlightedEntitiesMarkdown(highlighted.markdown, entities, active);
  }, [highlighted.markdown, entities, presentTypes, activeTypes, elem?.isStreaming]);

  const toggleType = type => setActiveTypes(prev => ({ ...prev, [type]: !prev[type] }));

  const markdownComponents = useMemo(
    () => ({
      ...MARKDOWN_COMPONENTS,
      mark: ({ node, 'data-cite': citeId, 'data-entity': entityId, dataCite, dataEntity, children }) => {
        if (entityId != null || dataEntity != null) {
          return (
            <EntityHighlight
              entity={entityHighlighted.entityById[entityId ?? dataEntity]}
              onOpen={entity => onSelectEntity?.({ entity, results: toolResults })}>
              {children}
            </EntityHighlight>
          );
        }
        return (
          <CitationHighlight source={highlighted.citeById[citeId ?? dataCite]} onOpen={onSelectSource}>
            {children}
          </CitationHighlight>
        );
      },
    }),
    [highlighted.citeById, entityHighlighted.entityById, onSelectSource, onSelectEntity, toolResults],
  );

  return (
    <div ref={rootRef} className="min-w-0 max-w-full">
      {elem.human && (
        <div className="heading user-text">
          <div className="icon-box" aria-hidden="true">
            {initial}
          </div>
          <strong className="title whitespace-pre-line">{elem?.human}</strong>
        </div>
      )}

      <div className="heading">
        {!elem?.delegate_agent && (
          <IntermediateStepsPanel steps={elem?.intermediate_steps} isLoading={elem?.isLoading} />
        )}

        {(hasSources || hasEntities) && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {hasSources && (
              <button
                type="button"
                onClick={() => setShowSources(value => !value)}
                className={toggleButtonClass(showSources)}>
                Sources
              </button>
            )}
            {presentTypes.map(type => {
              const Icon = TYPE_ICONS[type] || User;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleType(type)}
                  className={toggleButtonClass(activeTypes[type])}>
                  <Icon className="h-4 w-4" />
                  {ENTITY_TYPES[type]?.label}
                </button>
              );
            })}
          </div>
        )}

        {elem?.images && elem?.images?.length > 0 && elem?.images !== '' && (
          <div className="swiper-holder">
            <Swiper
              slidesPerView={4.2}
              spaceBetween={12}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
              modules={SWIPER_MODULES}
              breakpoints={{
                320: { slidesPerView: 1.3, spaceBetween: 10 },
                574: { slidesPerView: 2.2, spaceBetween: 12 },
                1250: { slidesPerView: 2.8, spaceBetween: 12 },
                1440: { slidesPerView: 3.2, spaceBetween: 14 },
                1750: { slidesPerView: 4.2, spaceBetween: 14 },
              }}>
              {elem?.images?.map((data, index) => (
                <SwiperSlide key={index}>
                  <a href={data?.origin_url} target="_blank" rel="noreferrer" className="img-wrap">
                    <img src={data?.image_url} alt="img" loading="lazy" />
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-controls">
              <button type="button" className="swiper-button-prev">
                <IoIosArrowBack />
              </button>
              <button type="button" className="swiper-button-next">
                <IoIosArrowForward />
              </button>
            </div>
          </div>
        )}

        {elem.ai && elem.ai !== '' && (
          <div className="ai-message-content">
            <Markdown
              remarkPlugins={REMARK_PLUGINS}
              rehypePlugins={REHYPE_PLUGINS}
              components={markdownComponents}>
              {entityHighlighted.markdown}
            </Markdown>
          </div>
        )}

        {!elem.ai &&
          !elem?.isLoading &&
          (user?.usage?.api_calls >= user?.plan?.request_limit ? (
            <span className="error-text">❌ Limit exceeded. Please upgrade your plan.</span>
          ) : (
            <span className="error-text">Something went wrong !!!</span>
          ))}
      </div>
    </div>
  );
};

export default memo(ChatMessageItem, (prev, next) => {
  if (next.isLast) return false;

  return prev.elem === next.elem && prev.ind === next.ind;
});
